import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Droplets, CloudLightning, CloudSnow, Wind, Clock, MapPin } from "lucide-react";
import { BRIGHT_BG, HATCH, SLIDE_MS } from "@/components/broadcast/shared";
import { groupAlerts, gaOnly, hazardColor, prefetchAlertMaps, AlertAreaMap, TvAlertsMap, VelocityAreaMap } from "@/components/broadcast/AlertMaps";
import { useCounties, TvOutlookMap } from "@/components/broadcast/TvMap";

const ALERT_ACCENT = {
  tornado: "#d90429", warning: "#d90429", severe: "#ffd60a",
  flood: "#2a9d8f", winter: "#4cc9f0", watch: "#f6a21c",
  advisory: "#eab308", default: "#1f63c9",
};
// Pick accent color + hazard icon + optional animated stripes from the alert type.
function alertTheme(alert) {
  const t = (alert.title || "").toLowerCase();
  const sev = (alert.severity || "").toLowerCase();
  if (t.includes("tornado")) return { accent: ALERT_ACCENT.tornado, stripes: true, icon: AlertTriangle };
  if (t.includes("flood")) return { accent: ALERT_ACCENT.flood, icon: Droplets };
  if (t.includes("thunderstorm")) return { accent: ALERT_ACCENT.severe, stripes: t.includes("warning"), icon: CloudLightning };
  if (t.includes("winter") || t.includes("snow") || t.includes("ice") || t.includes("freeze")) return { accent: ALERT_ACCENT.winter, icon: CloudSnow };
  if (t.includes("wind")) return { accent: ALERT_ACCENT.severe, icon: Wind };
  if (t.includes("warning")) return { accent: ALERT_ACCENT.warning, stripes: sev === "extreme", icon: AlertTriangle };
  if (t.includes("watch")) return { accent: ALERT_ACCENT.watch, icon: AlertTriangle };
  if (t.includes("advisory") || t.includes("statement")) return { accent: ALERT_ACCENT.advisory, icon: AlertTriangle };
  return { accent: ALERT_ACCENT.default, icon: AlertTriangle };
}
function fmtAlertUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", minute: "2-digit" }) + " ET";
}
function alertSafety(alert) {
  const t = (alert.title || "").toLowerCase();
  if (t.includes("tornado")) return "Take cover now — lowest floor, interior room, away from windows.";
  if (t.includes("flash flood") || t.includes("flood")) return "Turn around, don't drown. Never drive through flooded roads.";
  if (t.includes("thunderstorm")) return "Move indoors away from windows. Expect damaging winds and hail.";
  if (t.includes("hurricane") || t.includes("tropical")) return "Finish preparations now and follow local evacuation orders.";
  if (t.includes("extreme wind")) return "Treat this like a tornado — shelter in an interior room immediately.";
  if (t.includes("winter") || t.includes("ice") || t.includes("snow") || t.includes("freeze")) return "Slow down, allow extra travel time, and protect pipes and pets.";
  return "Stay weather aware and keep watching for updates.";
}
function AlertSlide({ alerts = [], alert: single, perMs = SLIDE_MS }) {
  const list = useMemo(() => groupAlerts(alerts.length ? alerts : (single ? [single] : [])), [alerts, single]);
  const counties = useCounties();
  useEffect(() => { prefetchAlertMaps(list, counties); }, [list, counties]);
  const [k, setK] = useState(0);
  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => setK((n) => (n + 1) % list.length), Math.max(3000, perMs));
    return () => clearInterval(t);
  }, [list.length, perMs]);
  const alert = list[Math.min(k, Math.max(list.length - 1, 0))];
  if (!alert) return null;
  const theme = alertTheme(alert);
  const accent = theme.accent;
  const light = ["#ffd60a", "#f6a21c", "#eab308", "#4cc9f0"].includes(accent);
  const ink = light ? "#0a0f1c" : "#fff";
  const until = fmtAlertUntil(alert.expires);
  const counties_ = gaOnly(alert.counties, counties);
  const mapColor = hazardColor(alert.title);
  const statusLabel = /warning/i.test(alert.title || "") ? "Warning in effect"
    : /watch/i.test(alert.title || "") ? "Watch in effect"
    : /advisory|statement/i.test(alert.title || "") ? "Advisory in effect" : "Alert in effect";
  const MAX_CHIPS = 24;

  return (
    <div data-testid="alert-slide" className="relative h-full w-full overflow-hidden" style={{ background: BRIGHT_BG }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: HATCH }} />
      {/* Accent panel sweeps in from the left (re-wipes on each alert in the cycle) */}
      <div key={`bg-${alert.id || k}`} className="absolute inset-0">
        <div className="alert-wipe absolute inset-y-0 left-0 w-[50%]" style={{ background: `linear-gradient(160deg, ${accent} 0%, ${accent}cc 100%)`, clipPath: "polygon(0 0, 100% 0, 88% 100%, 0 100%)" }} />
        {theme.stripes && <div className="alert-stripes absolute inset-y-0 left-0 w-[50%] opacity-30" style={{ clipPath: "polygon(0 0, 100% 0, 88% 100%, 0 100%)" }} />}
        <div className="alert-wipe absolute inset-y-0 left-[46%] w-[2.5%] bg-[#0a0f1c]" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)", animationDelay: "120ms" }} />
      </div>

      <div key={`c-${alert.id || k}`} className="relative flex h-full w-full items-stretch gap-10 px-[70px] pb-[64px]">
        {/* Left — alert details */}
        <div className="flex min-w-0 flex-1 flex-col justify-center" style={{ color: ink }}>
          <div className="flex items-center gap-3">
            <div className="inline-flex w-fit items-center gap-3 rounded-full bg-black/30 px-5 py-2 font-oswald text-[22px] font-bold uppercase tracking-[0.3em] text-white">
              <span className="live-dot" style={{ width: 12, height: 12 }} /> {(alert.source || "NWS")} · {statusLabel}
            </div>
            {list.length > 1 && (
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0a0f1c] px-4 py-2 font-oswald text-[20px] font-bold uppercase tracking-[0.2em] text-white" data-testid="alert-slide-counter">
                Alert {k + 1} <span className="opacity-50">of</span> {list.length}
              </div>
            )}
          </div>
          <div className="mt-5 font-oswald text-[100px] font-black uppercase leading-[0.9] tracking-wide drop-shadow-[0_10px_30px_rgba(0,0,0,0.25)]" data-testid="alert-slide-title">{alert.title}</div>
          {until && (
            <div className="mt-6 inline-flex w-fit items-center gap-3 rounded-[4px] bg-[#0a0f1c] px-5 py-2.5 font-oswald text-[30px] font-bold uppercase tracking-wider text-white" data-testid="alert-slide-until">
              <Clock className="h-7 w-7" style={{ color: "#ffd60a" }} /> In effect until {until}
            </div>
          )}
          {counties_.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-2 font-oswald text-[20px] font-bold uppercase tracking-[0.3em]" style={{ opacity: 0.85 }}><MapPin className="h-5 w-5" /> {counties_.length === 1 ? "Affected county" : `Affected counties (${counties_.length})`}</div>
              <div className="mt-2 flex max-w-[820px] flex-wrap gap-2" data-testid="alert-slide-areas">
                {counties_.slice(0, MAX_CHIPS).map((c, i) => <span key={`${c}-${i}`} className={`rounded-[3px] bg-white px-3 py-1 font-oswald font-bold uppercase tracking-wide text-[#0a0f1c] ${counties_.length > 12 ? "text-[17px]" : "text-[20px]"}`}>{c}</span>)}
                {counties_.length > MAX_CHIPS && <span className="rounded-[3px] bg-white/75 px-3 py-1 font-oswald text-[17px] font-bold uppercase text-[#0a0f1c]">+{counties_.length - MAX_CHIPS} more</span>}
              </div>
            </div>
          )}
          <div className="mt-7 w-fit max-w-[820px] rounded-[6px] bg-[#0a0f1c] px-7 py-5 text-white" data-testid="alert-slide-safety">
            <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.3em]" style={{ color: "#ffd60a" }}>What to do now</div>
            <div className="mt-1 font-oswald text-[32px] font-black uppercase leading-tight">{alertSafety(alert)}</div>
          </div>
        </div>

        {/* Right — broadcast map of every county under this alert */}
        <div className="flex w-[640px] shrink-0 flex-col justify-center">
          <div className="overflow-hidden rounded-[6px] bg-white shadow-[0_30px_80px_rgba(10,15,28,0.3)]">
            <div className="flex items-center justify-between px-6 py-3 font-oswald text-[20px] font-black uppercase tracking-[0.25em] text-white" style={{ background: "#0a0f1c" }}>
              <span>{counties_.length ? `${counties_.length} ${counties_.length === 1 ? "County" : "Counties"} · Georgia` : "Alert area · Georgia"}</span>
              <span className="flex items-center gap-2 text-[16px] font-bold tracking-widest text-white/70"><span className="live-dot" /> Live</span>
            </div>
            <div className="relative aspect-square w-full bg-[#cfd3d8]" data-testid="alert-polygon-inset">
              <AlertAreaMap key={alert.id} group={alert} className="absolute inset-0 h-full w-full" />
              <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-[3px] bg-white/95 px-4 py-2 font-oswald text-[18px] font-bold uppercase tracking-wider text-[#0a0f1c] shadow-[0_6px_20px_rgba(10,15,28,0.25)]" data-testid="alert-map-legend">
                <span className="h-5 w-7 rounded-sm border-2 border-[#0a0f1c]/60" style={{ background: mapColor }} /> {alert.title}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function GAAlertsMapSlide({ alerts }) {
  const groups = useMemo(() => groupAlerts(alerts || []), [alerts]);
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="ga-alerts-map-container">
      <TvAlertsMap groups={groups} />
    </div>
  );
}
function GASevereOutlookSlide({ days, durationMs = 12000 }) {
  const [k, setK] = useState(0);
  useEffect(() => {
    if (days.length < 2) return undefined;
    const t = setInterval(() => setK((n) => (n + 1) % days.length), Math.max(3500, Math.floor(durationMs / days.length)));
    return () => clearInterval(t);
  }, [days.length, durationMs]);
  const day = days[Math.min(k, days.length - 1)];
  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="spc-outlook-slide">
      <TvOutlookMap key={day.day} day={day} />
    </div>
  );
}

// Storm-relative velocity board — airs during a Tornado / Severe T-storm Warning. Zoomed to the warned
// polygon with the nearest NEXRAD's velocity; green = toward radar, red = away, a tight couplet = rotation.
function VelocitySlide({ velocity }) {
  if (!velocity?.active || !velocity.geometry || !velocity.radar) return null;
  const isTor = /tornado/i.test(velocity.event || "");
  const accent = isTor ? "#d90429" : "#ffa500";
  const until = fmtAlertUntil(velocity.expires);
  const area = (velocity.area || "").split(";")[0]?.trim();
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0f1c]" data-testid="velocity-slide">
      <VelocityAreaMap geometry={velocity.geometry} radar={velocity.radar} event={velocity.event} className="h-full w-full" />
      {/* Hazard header (top-right; the frame banner carries the "Base Velocity" title top-left) */}
      <div className="absolute right-[24px] top-[24px] flex max-w-[640px] flex-col items-end gap-2" data-testid="velocity-header">
        <div className="flex items-center gap-2.5 rounded-[3px] px-4 py-1.5 font-oswald text-[24px] font-black uppercase tracking-[0.14em] text-white" style={{ background: accent }}>
          <AlertTriangle className="h-7 w-7" strokeWidth={2.5} />
          {isTor ? "Radar-Indicated Rotation" : "Storm-Relative Velocity"}
        </div>
        {area && <div className="rounded-[3px] bg-[#0a0f1c]/85 px-3.5 py-1.5 font-oswald text-[22px] font-bold uppercase tracking-wide text-white backdrop-blur-sm">{area}</div>}
        {until && <div className="rounded-[3px] bg-[#0a0f1c]/85 px-3.5 py-1.5 font-oswald text-[20px] font-bold uppercase tracking-wide text-[#ffd60a] backdrop-blur-sm">Until {until}</div>}
      </div>
      {/* Radar + couplet legend */}
      <div className="absolute bottom-[64px] left-[24px] flex items-center gap-5 rounded-[3px] bg-[#0a0f1c]/88 px-5 py-2.5 font-oswald text-[20px] font-bold uppercase tracking-wider text-white backdrop-blur-sm" data-testid="velocity-legend">
        <span className="text-[#ffd60a]">{velocity.radar.id} · {velocity.radar.name}</span>
        <span className="flex items-center gap-2"><span className="h-5 w-7 rounded-sm" style={{ background: "#13d957" }} />Toward</span>
        <span className="flex items-center gap-2"><span className="h-5 w-7 rounded-sm" style={{ background: "#ff2b2b" }} />Away</span>
        <span className="opacity-80">Tight couplet = rotation</span>
      </div>
    </div>
  );
}

export { AlertSlide, GAAlertsMapSlide, GASevereOutlookSlide, VelocitySlide };
